/**
 * Deploy Phase — pure game logic for pre-game planet selection.
 * No Three.js imports. Deterministic given same inputs.
 */

import type { DeployState, DeploySelection, DeployRegion, PlayerState, PlanetState, Vec3, ModeConfig, ModeState, CrashedDropCanState } from '../types';
import type { SeededRandom } from './random';
import { hasLineOfSight } from './los';
import { vecLength, vecSub, vecDot, vecNormalize } from '../math';

// =============================================================================
// LANDING REGIONS (sector layer: the lane you arrived by shapes where you land)
// =============================================================================

/** A region facing a direction on the sector map's plane (dx, dy) — the lane from the previous system to this one. */
export function regionFromArrival(dx: number, dy: number, halfAngle?: number): DeployRegion {
  const len = dhypot(dx, dy);
  const facing: Vec3 = len > 1e-9 ? { x: dx / len, y: 0, z: dy / len } : { x: 1, y: 0, z: 0 };
  return halfAngle !== undefined ? { facing, halfAngle } : { facing };
}

function regionCos(region: DeployRegion): number {
  return dcos(region.halfAngle ?? Math.PI / 2);
}

/** Centre of the spawnable planets — the system's "middle" the region is measured from. */
export function spawnableCentroid(planets: PlanetState[]): Vec3 {
  let x = 0, y = 0, z = 0, n = 0;
  for (const p of planets) if (p.type === 'planet') { x += p.position.x; y += p.position.y; z += p.position.z; n++; }
  return n > 0 ? { x: x / n, y: y / n, z: z / n } : { x: 0, y: 0, z: 0 };
}

/**
 * Spawnable planets inside the region, best-facing first. Never empty when any planet is spawnable:
 * if none lies within the half-angle, the one facing the arrival most is allowed, so a team can always land.
 */
export function regionPlanets(region: DeployRegion, planets: PlanetState[], exclude: ReadonlySet<number> = new Set()): number[] {
  const c = spawnableCentroid(planets);
  const f = vecNormalize(region.facing);
  const minCos = regionCos(region);
  const scored: Array<{ i: number; cos: number }> = [];
  for (let i = 0; i < planets.length; i++) {
    if (planets[i].type !== 'planet' || exclude.has(i)) continue;
    const d = vecSub(planets[i].position, c);
    const len = vecLength(d);
    const cos = len > 1e-9 ? vecDot(d, f) / len : 1;
    scored.push({ i, cos });
  }
  scored.sort((a, b) => b.cos - a.cos || a.i - b.i);
  const inside = scored.filter(s => s.cos >= minCos - 1e-9).map(s => s.i);
  return inside.length > 0 ? inside : scored.slice(0, 1).map(s => s.i);
}

/** Whether a landing point on a planet faces the arrival (within the region's half-angle). */
export function surfaceDirInRegion(region: DeployRegion, surfaceDir: Vec3): boolean {
  return vecDot(vecNormalize(surfaceDir), vecNormalize(region.facing)) >= regionCos(region) - 1e-9;
}

/** A selection is valid for a region when its planet is allowed and its landing point faces the arrival. */
export function selectionInRegion(region: DeployRegion, planets: PlanetState[], planetIndex: number, surfaceDir: Vec3, exclude?: ReadonlySet<number>): boolean {
  return regionPlanets(region, planets, exclude).includes(planetIndex) && surfaceDirInRegion(region, surfaceDir);
}

/**
 * The random landing point every bot selection uses (two rng draws, unchanged); with a region the point
 * is reflected through the planet's centre when it faces away, so the draw count — and therefore every
 * selection made without a region — is exactly what it was.
 */
function randomSurfaceDir(rng: SeededRandom, region?: DeployRegion): Vec3 {
  const theta = rng.range(0, Math.PI * 2);
  const phi = rng.range(0.3, Math.PI - 0.3);
  let dir: Vec3 = { x: dsin(phi) * dcos(theta), y: dcos(phi), z: dsin(phi) * dsin(theta) };
  if (region && !surfaceDirInRegion(region, dir)) {
    const f = vecNormalize(region.facing);
    const d = vecDot(dir, f);
    // Reflect across the plane through the centre perpendicular to the facing: the mirror point faces the arrival
    dir = vecNormalize({ x: dir.x - 2 * d * f.x, y: dir.y - 2 * d * f.y, z: dir.z - 2 * d * f.z });
    if (!surfaceDirInRegion(region, dir)) dir = f;   // narrow half-angles: land straight at the arrival point
  }
  return dir;
}
import { getModeRules } from './modeRules';
import { dacos, datan2, dcos, dhypot, dsin } from './dmath';

/**
 * Initialize deploy state for the given teams.
 * Human teams select first, bot teams auto-select later.
 */
export function initDeployState(
  players: PlayerState[],
  numTeams: number,
  humanTeams: number,
): DeployState {
  return {
    selections: [],
    currentTeamIndex: 0,
    animating: false,
    done: false,
  };
}

/**
 * Record a planet selection for a team.
 * Returns updated DeployState with the new selection appended
 * and currentTeamIndex advanced to the next unselected human team.
 */
export function selectPlanet(
  state: DeployState,
  teamIndex: number,
  planetIndex: number,
  surfaceDir: Vec3,
  isBot: boolean,
): DeployState {
  const selection: DeploySelection = {
    teamIndex,
    planetIndex,
    surfaceDir,
    isBot,
  };

  const newSelections = [...state.selections, selection];

  return {
    ...state,
    selections: newSelections,
    currentTeamIndex: state.currentTeamIndex + 1,
  };
}

/**
 * Get the set of planet indices already selected by any team.
 */
export function getOccupiedPlanets(state: DeployState): Set<number> {
  const occupied = new Set<number>();
  for (const sel of state.selections) {
    occupied.add(sel.planetIndex);
  }
  return occupied;
}

/**
 * Auto-select planets for bot teams.
 * Picks random unoccupied spawnable (type='planet') planets.
 */
export function autoSelectBotPlanets(
  state: DeployState,
  planets: PlanetState[],
  players: PlayerState[],
  numTeams: number,
  humanTeams: number,
  rng: SeededRandom,
  regions?: ReadonlyArray<DeployRegion | undefined>,
  excluded: ReadonlySet<number> = new Set(),
): DeployState {
  const occupied = getOccupiedPlanets(state);

  // Get spawnable planet indices
  const spawnable: number[] = [];
  for (let i = 0; i < planets.length; i++) {
    if (planets[i].type === 'planet' && !occupied.has(i) && !excluded.has(i)) {
      spawnable.push(i);
    }
  }

  // Shuffle spawnable indices
  for (let i = spawnable.length - 1; i > 0; i--) {
    const j = rng.int(0, i);
    [spawnable[i], spawnable[j]] = [spawnable[j], spawnable[i]];
  }

  let updated = state;
  let spawnIdx = 0;

  // Find bot team indices (those >= humanTeams)
  for (let ti = humanTeams; ti < numTeams; ti++) {
    // Skip if already selected
    if (updated.selections.some(s => s.teamIndex === ti)) continue;

    const region = regions?.[ti];
    let planetIndex: number;
    if (region) {
      // Land on the arriving side: the first unoccupied planet of the shuffled order that the region allows,
      // else the region's best-facing planet (never nothing)
      const taken = getOccupiedPlanets(updated);
      const allowed = regionPlanets(region, planets, new Set([...excluded, ...taken]));
      planetIndex = spawnable.find((i, k) => k >= spawnIdx && allowed.includes(i)) ?? allowed[0] ?? 0;
      const k = spawnable.indexOf(planetIndex);
      if (k >= spawnIdx) { spawnable.splice(k, 1); spawnable.splice(spawnIdx, 0, planetIndex); spawnIdx++; }
    } else {
      planetIndex = spawnIdx < spawnable.length
        ? spawnable[spawnIdx++]
        : 0; // fallback
    }

    updated = selectPlanet(updated, ti, planetIndex, randomSurfaceDir(rng, region), true);
  }

  return updated;
}

/**
 * Check if all teams have made their selections.
 */
export function isDeployComplete(state: DeployState, numTeams: number): boolean {
  return state.selections.length >= numTeams;
}

/**
 * Apply deploy selections to players: update each player's planetIndex and surfaceDir
 * based on their team's selection. Teammates spread around the selected planet surface.
 */
export function applyDeployToPlayers(
  players: PlayerState[],
  selections: DeploySelection[],
  rng: SeededRandom,
): PlayerState[] {
  // Build team → selection map
  const teamSelections = new Map<number, DeploySelection>();
  for (const sel of selections) {
    teamSelections.set(sel.teamIndex, sel);
  }

  // Group players by team
  const teamPlayers = new Map<number, number[]>();
  for (let i = 0; i < players.length; i++) {
    const ti = players[i].teamIndex;
    const list = teamPlayers.get(ti) ?? [];
    list.push(i);
    teamPlayers.set(ti, list);
  }

  const updated = [...players];

  for (const [teamIndex, playerIndices] of teamPlayers) {
    const sel = teamSelections.get(teamIndex);
    if (!sel) continue;

    for (let ui = 0; ui < playerIndices.length; ui++) {
      const pi = playerIndices[ui];

      // Spread teammates around the selected surface point
      let surfaceDir: Vec3;
      if (playerIndices.length === 1) {
        surfaceDir = sel.surfaceDir;
      } else {
        // Offset each unit by rotating around the selection point
        const thetaOffset = (ui / playerIndices.length) * Math.PI * 2;
        const baseTheta = datan2(sel.surfaceDir.z, sel.surfaceDir.x);
        const basePhi = dacos(Math.max(-1, Math.min(1, sel.surfaceDir.y)));

        const theta = baseTheta + thetaOffset * 0.5; // moderate spread around selection point
        const phi = basePhi + rng.range(-0.15, 0.15);
        const clampedPhi = Math.max(0.1, Math.min(Math.PI - 0.1, phi));

        surfaceDir = {
          x: dsin(clampedPhi) * dcos(theta),
          y: dcos(clampedPhi),
          z: dsin(clampedPhi) * dsin(theta),
        };
      }

      updated[pi] = {
        ...updated[pi],
        planetIndex: sel.planetIndex,
        surfaceDir,
      };
    }
  }

  return updated;
}

// =============================================================================
// HOME PLANET ASSIGNMENT (CTF)
// =============================================================================

/**
 * Assign home planets to teams using greedy farthest-first selection.
 * For 2 teams: picks the pair of spawnable planets with maximum distance.
 * For N teams: greedily picks the planet farthest from all already-picked planets.
 */
export function assignHomeTeamPlanets(
  planets: PlanetState[],
  numTeams: number,
  rng: SeededRandom
): Record<number, number> {
  // Get spawnable planet indices
  const spawnable: number[] = [];
  for (let i = 0; i < planets.length; i++) {
    if (planets[i].type === 'planet' && planets[i].alive) {
      spawnable.push(i);
    }
  }

  if (spawnable.length === 0) return {};
  if (spawnable.length === 1) {
    const result: Record<number, number> = {};
    for (let t = 0; t < numTeams; t++) result[t] = spawnable[0];
    return result;
  }

  const result: Record<number, number> = {};
  const used = new Set<number>();

  if (numTeams === 2 && spawnable.length >= 2) {
    // Find pair with max distance
    let bestDist = -1;
    let bestA = spawnable[0];
    let bestB = spawnable[1];

    for (let i = 0; i < spawnable.length; i++) {
      for (let j = i + 1; j < spawnable.length; j++) {
        const dist = vecLength(vecSub(
          planets[spawnable[i]].position,
          planets[spawnable[j]].position
        ));
        if (dist > bestDist) {
          bestDist = dist;
          bestA = spawnable[i];
          bestB = spawnable[j];
        }
      }
    }

    result[0] = bestA;
    result[1] = bestB;
    return result;
  }

  // Greedy farthest-first for N teams
  // Start with a random planet
  const firstIdx = spawnable[rng.int(0, spawnable.length - 1)];
  result[0] = firstIdx;
  used.add(firstIdx);

  for (let t = 1; t < numTeams; t++) {
    let bestPlanet = spawnable[0];
    let bestMinDist = -1;

    for (const si of spawnable) {
      if (used.has(si)) continue;

      // Find minimum distance to any already-selected planet
      let minDist = Infinity;
      for (const usedIdx of used) {
        const dist = vecLength(vecSub(planets[si].position, planets[usedIdx].position));
        if (dist < minDist) minDist = dist;
      }

      if (minDist > bestMinDist) {
        bestMinDist = minDist;
        bestPlanet = si;
      }
    }

    result[t] = bestPlanet;
    used.add(bestPlanet);
  }

  return result;
}

// =============================================================================
// MODE-SPECIFIC DEPLOY RULES
// =============================================================================

/**
 * Determine the deploy mode based on game mode config.
 * - 'home': Teams deploy on their pre-assigned home planet (CTF).
 * - 'assigned': Teams deploy on their assigned depot/objective planet (Salvage Run).
 * - 'free': Teams choose any unoccupied planet (default).
 */
export function getDeployMode(modeConfig: ModeConfig): 'free' | 'home' | 'assigned' {
  switch (getModeRules(modeConfig).objective) {
    case 'ctf': return 'home';
    case 'salvage': return 'assigned';
    default: return 'free';
  }
}

/**
 * Get planets that should be excluded from deploy selection for a given mode.
 * E.g., the excavation objective excludes the artifact planet.
 */
export function getExcludedPlanets(_modeConfig: ModeConfig, modeState: ModeState): Set<number> {
  const excluded = new Set<number>();

  if (modeState.excavation) {
    excluded.add(modeState.excavation.artifactPlanetIndex);
  }

  return excluded;
}

/**
 * Auto-select bot planets with mode-specific rules.
 * - CTF: bots deploy on their home planet.
 * - SalvageRun: bots deploy on their team's depot planet.
 * - Others: random selection, respecting excluded planets.
 */
export function autoSelectBotPlanetsForMode(
  state: DeployState,
  planets: PlanetState[],
  players: PlayerState[],
  numTeams: number,
  humanTeams: number,
  rng: SeededRandom,
  modeConfig: ModeConfig,
  modeState: ModeState,
  homeTeamPlanets?: Record<number, number>,
  /** Per team: where it may land (the sector's arrival lane). Home / depot planets stay fixed; the landing point still faces the arrival. */
  regions?: ReadonlyArray<DeployRegion | undefined>,
): DeployState {
  const deployMode = getDeployMode(modeConfig);

  // Home deploy: bots go to their assigned home planet
  if (deployMode === 'home' && homeTeamPlanets) {
    let updated = state;
    for (let ti = humanTeams; ti < numTeams; ti++) {
      if (updated.selections.some(s => s.teamIndex === ti)) continue;
      const planetIndex = homeTeamPlanets[ti] ?? 0;
      updated = selectPlanet(updated, ti, planetIndex, randomSurfaceDir(rng, regions?.[ti]), true);
    }
    return updated;
  }

  // Assigned deploy: bots go to their depot planet (salvage run)
  if (deployMode === 'assigned' && modeState.salvage) {
    let updated = state;
    for (let ti = humanTeams; ti < numTeams; ti++) {
      if (updated.selections.some(s => s.teamIndex === ti)) continue;
      const planetIndex = modeState.salvage.depotPlanets[ti] ?? 0;
      updated = selectPlanet(updated, ti, planetIndex, randomSurfaceDir(rng, regions?.[ti]), true);
    }
    return updated;
  }

  // Free deploy, respecting the mode's excluded planets and each team's landing region
  const excluded = getExcludedPlanets(modeConfig, modeState);
  return autoSelectBotPlanets(state, planets, players, numTeams, humanTeams, rng, regions, excluded);
}

// =============================================================================
// DROP + FAN-OUT (pure; src/game/deployPhase.ts animates these)
// =============================================================================

/** Whether a team's drop can is shown during the deploy animation (hideEnemyDeploy hides non-local-human teams). */
export function deployTeamIsVisible(
  teamIndex: number,
  players: Array<{ isBot: boolean; teamIndex: number }>,
  hideEnemyDeploy: boolean,
): boolean {
  if (!hideEnemyDeploy) return true;
  return players.some(p => !p.isBot && p.teamIndex === teamIndex);
}

export const DEPLOY_FAN_OUT_DISTANCE = 1.0; // arc units each unit walks away from the can

/**
 * Under fog, an enemy drop can (flying or just down) shows only while one of the
 * viewer team's own cans — airborne or landed — has a clear line to it. No eyes
 * yet (our cans have not launched) → nothing is seen.
 */
export function deployCanSeenFrom(viewerEyes: Vec3[], canPos: Vec3, planets: PlanetState[]): boolean {
  for (const eye of viewerEyes) if (hasLineOfSight(eye, canPos, planets)) return true;
  return false;
}
/** How often (s) a flying can scans the terrain beneath it for its team. */
export const DEPLOY_SCAN_INTERVAL = 0.1;
export const DROP_CAN_HEALTH = 50;

export interface FanOutTarget {
  playerId: number;
  planetIndex: number;
  startDir: Vec3;   // the can's landing spot
  endDir: Vec3;     // where this unit ends up
}

/**
 * After the drop cans land, each team's units stand at the can and walk a
 * short way outward in evenly spaced directions. Returns where each unit ends
 * up; players must already be placed on their planets (applyDeployToPlayers).
 */
export function computeFanOutTargets(
  players: PlayerState[],
  selections: DeploySelection[],
  planets: PlanetState[],
  distance: number = DEPLOY_FAN_OUT_DISTANCE,
): FanOutTarget[] {
  const landing = new Map<number, Vec3>();
  for (const sel of selections) landing.set(sel.teamIndex, sel.surfaceDir);
  const byTeam = new Map<number, PlayerState[]>();
  for (const p of players) byTeam.set(p.teamIndex, [...(byTeam.get(p.teamIndex) ?? []), p]);

  const out: FanOutTarget[] = [];
  for (const [teamIndex, members] of byTeam) {
    const n = landing.get(teamIndex);
    if (!n) continue;
    // Tangent basis at the landing point
    const arbitrary: Vec3 = Math.abs(n.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
    const t1 = normalizeVec({
      x: n.y * arbitrary.z - n.z * arbitrary.y,
      y: n.z * arbitrary.x - n.x * arbitrary.z,
      z: n.x * arbitrary.y - n.y * arbitrary.x,
    });
    const t2: Vec3 = { x: n.y * t1.z - n.z * t1.y, y: n.z * t1.x - n.x * t1.z, z: n.x * t1.y - n.y * t1.x };

    members.forEach((p, i) => {
      const planet = planets[p.planetIndex];
      const theta = distance / planet.radius;             // arc length → angle
      const alpha = (i / members.length) * Math.PI * 2;   // evenly spaced around the can
      const ct = dcos(theta), st = dsin(theta);
      const ca = dcos(alpha), sa = dsin(alpha);
      const endDir = normalizeVec({
        x: n.x * ct + (t1.x * ca + t2.x * sa) * st,
        y: n.y * ct + (t1.y * ca + t2.y * sa) * st,
        z: n.z * ct + (t1.z * ca + t2.z * sa) * st,
      });
      out.push({ playerId: p.id, planetIndex: p.planetIndex, startDir: { ...n }, endDir });
    });
  }
  return out;
}

/** Crashed drop cans become destructible scenery: one state entry per team, ids continuing from `firstId`. */
export function crashedDropCanStates(selections: DeploySelection[], firstId: number): CrashedDropCanState[] {
  return selections.map((sel, i) => ({
    id: firstId + i,
    teamIndex: sel.teamIndex,
    planetIndex: sel.planetIndex,
    surfaceDir: sel.surfaceDir,
    health: DROP_CAN_HEALTH,
  }));
}

function normalizeVec(v: Vec3): Vec3 {
  const l = dhypot(v.x, v.y, v.z) || 1;
  return { x: v.x / l, y: v.y / l, z: v.z / l };
}
